import React, { useState, useEffect } from "react"
import { Box, Grid, TextField, MenuItem, Input, Icon, Divider, ListSubheader } from "@material-ui/core"
import { CodeOutlined } from "@material-ui/icons"
import userflow from "userflow.js"
import moment from "moment"
import Validator from "validatorjs"
import Config from "react-global-configuration"
import { useApolloClient } from "@apollo/client"
import { CreatorActions, CreatorMaster } from "../Creators"
import { UserPanel } from "../UserPanel"
import { useFormUtils, toId } from "../../utils"
import { FieldSectionHeading } from "../Headings"
import {
  useQueryGroups,
  useQueryRoles,
  useMutationUpdateUser,
  useMutationCreateUser,
  USER_STATUS,
  useMutationCreateExternalUser,
  useMutationResendInvite,
} from "../../data"
import { useAuth } from "../../services"
import {
  TileButton,
  ColumnBox,
  RowBox,
  OutlinedSelect,
  GroupCreator,
  useSnackbar,
  IdentityProviderOutlinedSelect,
  LocationOutlinedSelect,
} from ".."
import { LinkButton } from "../LinkButton"
import { FormatDateTimeCompact } from "../Format"
import TimeAgo from "../TimeAgo"
import { TrainingOutlinedSelect } from "../Training"

const ExternalSwitcher = ({ external, onChange, variant = "single" }) => {
  const handleChange = (value) => {
    onChange && onChange(value)
  }

  const isPlural = variant === "plural"
  const append = isPlural ? "s" : ""

  return (
    <>
      <Box mb={2}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <ColumnBox flexGrow={1}>
              <TileButton
                title={`Local user${append}`}
                description={`The user${append} will be invited by email and will sign in using an email address and password / PIN`}
                active={!external}
                onClick={() => handleChange(false)}
                style={{ width: "100%" }}
                data-cy="TileButton-internal"
              />
            </ColumnBox>
          </Grid>
          <Grid item xs={12} md={6}>
            <TileButton
              title={`Single sign-on user${append}`}
              description={`Prepare settings for ${
                isPlural ? "users" : "a user"
              } that will sign in using your single sign-on identity provider`}
              active={external}
              onClick={() => handleChange(true)}
              style={{ width: "100%" }}
              data-cy="TileButton-external"
            />
          </Grid>
        </Grid>
      </Box>
      <Box mb={2}>
        <Divider />
      </Box>
    </>
  )
}

const PersonCreator = ({ edit, onClose, isInline }) => {
  const { isValid, debounceTouched } = useFormUtils()
  const {
    support: {
      userflow: { enabled: userflowEnabled },
    },
  } = Config.get()
  const { isCurrentUser, hasFeature, refresh } = useAuth()
  const client = useApolloClient()
  const snackbar = useSnackbar()

  const [updateUser, { loading: updateUserLoading }] = useMutationUpdateUser()
  const [createUser, { loading: createUserLoading }] = useMutationCreateUser()
  const [createExternalUser, { loading: createExternalUserLoading }] = useMutationCreateExternalUser()
  const [resendInvite] = useMutationResendInvite()
  const { data: groupsData, refetch: refetchGroups } = useQueryGroups()
  const { data: rolesData } = useQueryRoles()

  const loading = !groupsData || !rolesData
  const [touched, setTouched] = useState(false)
  const [external, setExternal] = useState(false)
  const [hasEmail, setHasEmail] = useState(false)
  const [identityProvider, setIdentityProvider] = useState("")
  const [unique, setUnique] = useState("")
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [email, setEmail] = useState("")
  const [groups, setGroups] = useState([])
  const [locations, setLocations] = useState([])
  const [trainingCourse, setTrainingCourse] = useState("")
  const [roles, setRoles] = useState("")
  const [status, setStatus] = useState(false)

  const [emailTouched, setEmailTouched] = useState(false)
  const [emailValid, setEmailValid] = useState(false)

  const [hasCourses, setHasCourses] = useState(true)
  const [openCreateGroup, setOpenCreateGroup] = useState(false)

  const currentUser = edit && isCurrentUser(edit.id)

  const canChangeStatus = edit && edit.status !== USER_STATUS.INITIAL

  const isInitial = edit && USER_STATUS.INITIAL === edit.status
  const isInvite = edit && [USER_STATUS.INVITED, USER_STATUS.INITIAL].includes(edit.status)
  const isInviteExpired = isInvite && moment().isAfter(moment(edit.invite.expiresAt))

  const activated = edit && edit.invite.activatedAt !== null

  useEffect(() => {
    if (edit) {
      setIdentityProvider(edit.external ? toId(edit.identityProvider) : "")
      setUnique(edit.unique)
      setFirstName(edit.firstName)
      setLastName(edit.lastName)
      setEmail(edit.email)
      setHasEmail(!edit.external || (edit.external && !edit.email.endsWith(toId(edit.identityProvider))))
      setEmailValid(new Validator({ value: edit.email }, { value: "required|email" }).passes())
      setGroups(edit.groups.map((item) => toId(item)))
      setLocations(edit.locations.map((item) => toId(item)))
      setRoles(edit.roles.length ? toId(edit.roles[0]) : "user")
      setStatus(edit.status)
      setExternal(edit.external)
    }
  }, [edit])

  const handleClose = (event, isCancel) => {
    onClose(isCancel)
  }

  const handleChange = (set, oldValue, newValue) => {
    if (newValue !== oldValue) {
      if (!touched) setTouched(true)
      set(newValue)
    }
  }

  const handleEmailChange = (e) => {
    const value = e.target.value
    setEmail(value)
    setEmailValid(new Validator({ value }, { value: "required|email" }).passes())
  }

  const handleTrainingCourseChange = (event) => {
    setTrainingCourse(event.target.value)
  }

  const handleChangeMultiple = (set, value) => {
    if (!touched) setTouched(true)
    set(value.filter((item) => item !== "new"))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (formValid()) {
      let result
      if (edit) {
        result = await updateUser({
          variables: {
            id: edit.id,
            firstName,
            lastName,
            groups,
            locations,
            roles: roles === "user" ? [] : [roles],
            status,
          },
        })
        // is me?
        if (isCurrentUser(edit.id)) {
          refresh(client, false)
        }
      } else if (external) {
        result = await createExternalUser({
          variables: {
            identityProvider,
            unique,
            firstName,
            lastName,
            groups,
            locations,
            trainingCourse,
            roles: roles === "user" ? [] : [roles],
          },
        })
        if (userflowEnabled) userflow.track("created_user")
      } else {
        result = await createUser({
          variables: {
            firstName,
            lastName,
            email,
            groups,
            locations,
            trainingCourse,
            roles: roles === "user" ? [] : [roles],
            status,
            invite: true,
          },
        })
        if (userflowEnabled) userflow.track("created_user")
      }
      if (result.errors?.length) {
        snackbar.showMessage({ message: result.errors[0].message, icon: <CodeOutlined />, color: "secondary" })
        return
      }
      handleClose()
    }
  }

  const handleResendInvite = () => {
    resendInvite({ variables: { id: toId(edit) } })
  }

  const handleCoursesDataLoaded = (coursesData) => {
    setHasCourses(Boolean(coursesData?.length))
  }

  const handleCreateGroupClose = async (updated) => {
    setOpenCreateGroup(false)
    if (updated) await refetchGroups()
  }

  const handleRegionChange = (newLocations) => {
    handleChangeMultiple(setLocations, newLocations)
  }

  const externalEdit = edit && external

  const createExternal = !edit && external

  const formLocalValid = () =>
    locations.length && isValid(firstName, lastName, email, roles) && (external || emailValid)

  const formCreateExternalValid = () =>
    locations.length && isValid(identityProvider, unique, firstName, lastName, roles)

  const formValid = () => (createExternal ? formCreateExternalValid() : formLocalValid())

  const isFormValid = formValid()

  const hasFeatureIdentityProviders = hasFeature("identity_providers")

  const hasCustomRoles = rolesData?.roles.some((role) => role.isCustom)

  const form = (
    <>
      <GroupCreator open={openCreateGroup} onClose={handleCreateGroupClose} />

      {!edit && hasFeatureIdentityProviders && <ExternalSwitcher external={external} onChange={setExternal} />}

      {edit && <UserPanel user={edit} layout="landscape" mb={4} mr={0} />}

      <Box mb={3}>
        <Grid container spacing={2}>
          {external && (
            <>
              <Grid item xs={12}>
                <IdentityProviderOutlinedSelect
                  value={identityProvider}
                  onChange={(event) => handleChange(setIdentityProvider, identityProvider, event.target.value)}
                  disabled={loading || !!edit}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  variant="outlined"
                  fullWidth
                  label="User ID"
                  value={unique}
                  onChange={(event) => handleChange(setUnique, unique, event.target.value)}
                  required
                  disabled={loading || !!edit}
                  inputProps={{ "data-cy": "TextField-unique" }}
                />
                <Box mt={0.25} mx={1.75}>
                  <small>
                    User ID is the unique identifier assigned by your identity provider. Often called User ID or Object
                    ID (Azure AD).
                    <br />
                    See your identity providers documentation for further guidance.
                  </small>
                </Box>
              </Grid>
            </>
          )}

          <Grid item xs={12} sm={6}>
            <TextField
              variant="outlined"
              fullWidth
              label="First name"
              value={firstName}
              onChange={(event) => handleChange(setFirstName, firstName, event.target.value)}
              required
              disabled={loading || externalEdit}
              inputProps={{ "data-cy": "TextField-firstname" }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              variant="outlined"
              fullWidth
              label="Last name"
              value={lastName}
              onChange={(event) => handleChange(setLastName, lastName, event.target.value)}
              required
              disabled={loading || externalEdit}
              inputProps={{ "data-cy": "TextField-lastname" }}
            />
          </Grid>
          {!createExternal && (!edit || hasEmail) && (
            <Grid item xs={12}>
              <TextField
                variant="outlined"
                fullWidth
                label="Email address"
                value={email}
                onChange={handleEmailChange}
                onBlur={() => debounceTouched(setEmailTouched)}
                error={emailTouched && !emailValid}
                required
                disabled={loading || !!edit}
                inputProps={{ "data-cy": "TextField-email" }}
              />
              {!!edit && (
                <RowBox>
                  <Box ml="auto">
                    {!externalEdit && <small>Contact support for username / email address changes</small>}
                    {externalEdit && (
                      <small>This user is managed externally, editing is disabled for some fields</small>
                    )}
                  </Box>
                </RowBox>
              )}
            </Grid>
          )}
        </Grid>
      </Box>

      <FieldSectionHeading size="large">Work &amp; location</FieldSectionHeading>

      <Box mb={3}>
        <Grid container spacing={2}>
          <Grid item xs={12} id="areas-of-work-grid-item">
            <OutlinedSelect
              label="Areas of work"
              id="areas-of-work"
              native={false}
              value={groups}
              onChange={(event) => handleChangeMultiple(setGroups, event.target.value)}
              input={<Input />}
              multiple
              menuPortal={false}
              disabled={loading}
              data-cy="OutlinedSelect-groups"
            >
              <MenuItem value="new" onClick={() => setOpenCreateGroup(true)} data-cy="OutlinedSelect-groups-new">
                <RowBox>
                  <Icon>add</Icon>
                  <Box ml={1}>New area of work...</Box>
                </RowBox>
              </MenuItem>
              {groupsData && groupsData.groups.length > 0 && <Divider />}
              {groupsData &&
                groupsData.groups.map((item, index) => (
                  <MenuItem key={item.id} value={item.id} data-cy={`OutlinedSelect-group-${index}`}>
                    {item.name}
                  </MenuItem>
                ))}
            </OutlinedSelect>
          </Grid>
          <Grid item xs={12}>
            <LocationOutlinedSelect
              mode="admin"
              value={locations}
              onChange={(event) => handleChangeMultiple(setLocations, event.target.value)}
              onRegionChange={handleRegionChange}
              hasAll={false}
              multiple
              required
            />
          </Grid>
          {!edit && hasCourses && (
            <Grid item xs={12}>
              <TrainingOutlinedSelect
                value={trainingCourse}
                onChange={handleTrainingCourseChange}
                onDataLoaded={handleCoursesDataLoaded}
              />
            </Grid>
          )}
        </Grid>
      </Box>

      <FieldSectionHeading size="large">Permissions {edit && <>&amp; status</>}</FieldSectionHeading>

      <Box mb={isInvite ? 1 : 3}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={edit && canChangeStatus ? 6 : 12}>
            <OutlinedSelect
              label="Permission level"
              id="permission-level"
              native={false}
              value={roles}
              onChange={(event) => handleChange(setRoles, roles, event.target.value)}
              disabled={currentUser}
              menuPortal={false}
              required
              data-cy="OutlinedSelect-roles"
            >
              {hasCustomRoles && <ListSubheader disableSticky>Built-in roles</ListSubheader>}
              {rolesData &&
                [{ id: "user", friendlyName: "Employee" }, ...rolesData.roles.filter((role) => !role.isCustom)].map(
                  (item, index) => (
                    <MenuItem key={item.id} value={item.id} data-cy={`OutlinedSelect-role-${index}`}>
                      {item.friendlyName}
                    </MenuItem>
                  )
                )}
              {hasCustomRoles && <Divider />}
              {hasCustomRoles && <ListSubheader disableSticky>Custom roles</ListSubheader>}
              {rolesData?.roles
                .filter((role) => role.isCustom)
                .map((item, index) => (
                  <MenuItem key={item.id} value={item.id} data-cy={`OutlinedSelect-role-${index}`}>
                    {item.friendlyName}
                  </MenuItem>
                ))}
            </OutlinedSelect>
          </Grid>

          {canChangeStatus && (
            <Grid item xs={12} sm={6}>
              <OutlinedSelect
                label="Status"
                id="status"
                native={false}
                value={status}
                onChange={(event) => handleChange(setStatus, status, event.target.value)}
                disabled={currentUser}
                menuPortal={false}
                data-cy="OutlinedSelect-status"
              >
                {[USER_STATUS.ACTIVE, USER_STATUS.INACTIVE].includes(edit.status) && activated && (
                  <MenuItem value={USER_STATUS.ACTIVE}>Active</MenuItem>
                )}
                {[USER_STATUS.INVITED, USER_STATUS.INACTIVE].includes(edit.status) &&
                  !activated &&
                  !isInviteExpired && <MenuItem value={USER_STATUS.INVITED}>Invited</MenuItem>}
                {isInviteExpired && <MenuItem value={USER_STATUS.INVITED}>Expired</MenuItem>}
                <MenuItem value={USER_STATUS.INACTIVE}>Inactive</MenuItem>
              </OutlinedSelect>
            </Grid>
          )}
        </Grid>
      </Box>

      {isInvite && (
        <RowBox mb={3} justifyContent="flex-end">
          {isInviteExpired && (
            <Box mr={1}>
              Invite expired <FormatDateTimeCompact value={edit.invite.expiresAt} />
            </Box>
          )}
          {!isInviteExpired && Boolean(edit.invite?.sentAt) && (
            <Box mr={1}>
              Invite sent <TimeAgo date={edit.invite.sentAt} lowercase />, expires{" "}
              <FormatDateTimeCompact value={edit.invite.expiresAt} />
            </Box>
          )}
          <LinkButton onClick={handleResendInvite}>
            {isInitial && <>Send invite</>}
            {!isInitial && <>Resend invite</>}
          </LinkButton>
        </RowBox>
      )}

      <CreatorActions
        id="PersonCreator-CreatorActions"
        subject="Person"
        cancelLabel={edit ? "Back to profile" : "Cancel"}
        submitLabel={edit || createExternal ? null : "Send invitation"}
        submitLoading={createUserLoading || createExternalUserLoading || updateUserLoading}
        onClose={(event) => handleClose(event, true)}
        onSubmit={handleSubmit}
        disableSubmit={!isFormValid}
      />
    </>
  )

  const title = edit ? <>Edit personnel details</> : <>Add single person</>

  return <CreatorMaster open title={title} form={form} isInline={isInline} onClose={handleClose} />
}

export { PersonCreator, ExternalSwitcher as PersonCreatorExternalSwitcher }
